import { logger } from 'client/utils/logger/logger';
import { cookie } from 'client/utils/cookie/cookie';
import { TIME } from './time';
import experimentCookieConfig from './experiments-config.json';

/**
 * Loads settings from config file.
 *
 * @returns {Object}  An object with fields base, baseSet, eDigits, rDigits and maxExposureRecordLength
 */
const loadBaseSettings = () => {
  const { base, letters, eDigits, rDigits, maxTuplesCount, cookieName, showRecipeNotFoundError } =
    experimentCookieConfig;
  const baseSet = [];
  const maxExposureRecordLength = maxTuplesCount * (eDigits + rDigits);
  baseSet[base] = letters;

  return { base, baseSet, eDigits, rDigits, maxExposureRecordLength, cookieName, showRecipeNotFoundError };
};

const { base, baseSet, eDigits, rDigits, maxExposureRecordLength, cookieName, showRecipeNotFoundError } =
  loadBaseSettings();

/**
 * Converts given number to its string representation
 * using base and a set of symbols for base and using
 * only given number of digits.
 *
 * @param {Number} num      Number to convert
 * @param {Number} digits   Number of digits or basically the length of result string
 * @returns {String}        String representation of a number
 */
const intToBaseString = (num, digits) => {
  if (num >= base ** digits) {
    throw new Error(`Encoded number takes more than ${digits} digits`);
  }
  let result = '';
  let i = num;

  for (let p = digits - 1; p >= 0; p -= 1) {
    const bpv = base ** p;
    const pv = Math.floor(i / bpv);

    i -= pv * bpv;
    result += baseSet[base][pv];
  }

  return result;
};

/**
 * Converts given string to a number using base and
 * a set of symbols for base.
 *
 * @param {String} str  String representation of a number
 * @returns {Number}    An integer number
 */
const baseStringToInt = str =>
  str
    .split('')
    .reverse()
    .reduce((prev, curr, index) => prev + baseSet[base].indexOf(curr) * base ** index, 0);

/**
 * Maps experiment and its recipe names to ids.
 *
 * @param {Object} experimentObject         An object with fields: experimentName, recipeName, experimentId, recipeId
 * @param {Object} publishedExperiments    An object (each field contains an object with fields for an experiment)
 * @returns {Array[Number]}                An array with 2 items: experiment id and a recipe id
 */
const convertExperimentObjectToTuple = (experimentObject, publishedExperiments) => {
  if (!experimentObject) return undefined;
  const { experimentName, recipeName } = experimentObject;
  const experimentRecord = publishedExperiments[experimentName];
  if (!experimentRecord) return undefined;

  const recipeIndex = experimentRecord.recipeNames?.indexOf(recipeName);
  const recipeId = experimentRecord.recipeIds?.[recipeIndex];

  if (recipeId !== undefined) return [experimentRecord.id, recipeId];
  logger.error(
    `Recipe record with name ${recipeName} from campaign with id ${experimentRecord.id} not found in experiment data`
  );
  return undefined;
};

/**
 * Encodes the array of experiments and recipes names into the exposure record with a header.
 *
 * @param {Array[Object]} experimentsToEncode   An array of objects with fields: experimentId, experimentName, recipeId, recipeName
 * @returns {String}                            An exposure record with a header
 */
const encodeExposureRecord = experimentsToEncode => {
  const experimentsString = experimentsToEncode
    .reduce((resultArray, experimentObject) => {
      const { experimentId, recipeId } = experimentObject;
      if (experimentId === undefined) return resultArray;
      if (recipeId === undefined) {
        const { recipeName } = experimentObject;
        logger.error(
          `Recipe record with name ${recipeName} from campaign with id ${experimentId} not found in experiment data`
        );
        return resultArray;
      }

      const campaignRecord = intToBaseString(experimentObject.experimentId, eDigits);
      const recipeRecord = intToBaseString(experimentObject.recipeId, rDigits);
      resultArray.push(`${campaignRecord}${recipeRecord}`);
      return resultArray;
    }, [])
    .join('');
  const header = eDigits.toString() + rDigits.toString();

  return `${header}${experimentsString}`;
};

/**
 * Decodes the exposure record and returns an array of objects with experiments.
 *
 * @param {String} exposureRecord         An exposure record with a header
 * @param {Object} publishedExperiments   An object (each field contains an object with fields for an experiment)
 * @param {Boolean} keepNotFound          If experiment not found in experiments data then save experiment and recipes ids instead of names
 * @returns {Array[Object]}               An array of objects with fields: experimentId, experimentName, recipeId, recipeName
 */
const decodeExposureRecord = (exposureRecord, publishedExperiments, keepNotFound = false) => {
  if (exposureRecord === '' || exposureRecord === undefined) return [];
  const recEdigits = Number(exposureRecord[0]);
  const recRdigits = Number(exposureRecord[1]);
  const campaignsAndRecipes = exposureRecord.slice(2);
  const tupleLength = recEdigits + recRdigits;
  const records = [];

  if (campaignsAndRecipes.length % tupleLength !== 0) {
    logger.error(`Length of exposure record ${campaignsAndRecipes} is not divisible by ${tupleLength}`);
    return [];
  }

  for (let i = 0; i < campaignsAndRecipes.length; i += tupleLength) {
    records.push(campaignsAndRecipes.slice(i, i + tupleLength));
  }

  return records.reduce((decodedExperiments, currentRecord) => {
    if (currentRecord.length !== tupleLength) {
      logger.error(`Unexpected extra information ${currentRecord} in exposure record ${exposureRecord}`);

      return decodedExperiments;
    }

    const experimentCode = currentRecord.slice(0, recEdigits);
    const recipeCode = currentRecord.slice(recEdigits, tupleLength);
    const experimentId = baseStringToInt(experimentCode);
    const recipeId = baseStringToInt(recipeCode);
    const experimentRecord = Object.values(publishedExperiments).find(experiment => experiment.id === experimentId);

    if (!experimentRecord && !keepNotFound) return decodedExperiments;
    if (!experimentRecord && keepNotFound) {
      decodedExperiments.push({ experimentId, recipeId });
      return decodedExperiments;
    }

    const experimentName = experimentRecord?.name;
    const recipeIndex = experimentRecord?.recipeIds?.indexOf(recipeId);
    const recipeName = experimentRecord?.recipeNames?.[recipeIndex];

    decodedExperiments.push({
      experimentName,
      recipeName,
      experimentId,
      recipeId,
    });

    if (!recipeName && showRecipeNotFoundError) {
      logger.error(
        `Recipe record with id ${recipeId} from campaign with name ${experimentName} not found in experiment data`
      );
    }

    return decodedExperiments;
  }, []);
};

/**
 * Merges two exposure records together (and removing duplicates) and returns
 * the result string as an exposure record with a header.
 *
 * @param {String} firstRecord      An exposure record with a header
 * @param {String} secondRecord     An exposure record with a header
 * @returns {String}                An exposure record with a header
 */
const mergeExposureRecords = (firstRecord = '', secondRecord = '') => {
  const header = `${eDigits.toString()}${rDigits.toString()}`;
  const tupleLength = Number(header[0]) + Number(header[1]);
  const bothRecords = `${firstRecord.slice(2)}${secondRecord.slice(2)}`;
  const records = [];

  for (let i = 0; i < bothRecords.length; i += tupleLength) {
    records.push(bothRecords.slice(i, i + tupleLength));
  }

  // converting to Set and then to Array to remove duplicates
  const exposureRecordBody = Array.from(new Set(records)).join('');

  return exposureRecordBody === '' ? '' : `${header}${exposureRecordBody}`;
};

/**
 * Inserts a single ExperimentName, RecipeName tuple into the exposure record.
 *
 * @param {String} experimentName         experiment name
 * @param {String} recipeName             recipe name
 * @param {Object} publishedExperiments   An object (each field contains an object with fields for an experiment)
 * @param {String} exposureRecord         An exposure record with a header
 * @returns {String}                      An exposure record with a header
 */
const insertExperimentTuple = (experimentName, recipeName, publishedExperiments, exposureRecord) => {
  const decodedExperiments = decodeExposureRecord(exposureRecord, publishedExperiments, true);
  const experimentToInsert = publishedExperiments[experimentName];
  if (!experimentToInsert) {
    logger.error('insertExperimentTuple: the experiment was not found! Details: ');
    logger.error(
      JSON.stringify({
        experimentToInsert,
        experimentName,
        recipeName,
        exposureRecord,
      })
    );
  }

  const experimentIndex = decodedExperiments.findIndex(
    record => record.experimentId === experimentToInsert?.id || record.experimentName === experimentName
  );

  const experimentObject = {
    experimentName,
    experimentId: experimentToInsert?.id,
    recipeName,
    recipeId: experimentToInsert?.recipeIds?.[experimentToInsert?.recipeNames?.indexOf(recipeName)],
  };
  // check if recipe id exists in experimentsData
  if (!convertExperimentObjectToTuple(experimentObject, publishedExperiments)) {
    return exposureRecord;
  }

  let experimentsToEncode = [experimentObject, ...decodedExperiments];
  if (experimentIndex >= 0) {
    // pop it from current position in exposure record and insert in the first position
    experimentsToEncode = [
      experimentObject,
      ...decodedExperiments.slice(0, experimentIndex),
      ...decodedExperiments.slice(experimentIndex + 1),
    ];
  }

  const encodedString = encodeExposureRecord(experimentsToEncode);
  // as encodeExposureRecord returns the string already with a 2-character header, we have to cut it too
  return encodedString.slice(0, maxExposureRecordLength + 2);
};

/**
 * Sets the cookie with predefined options
 *
 * @param {String} exposureRecord   An exposure record with a header
 */
const setExposureRecordCookie = exposureRecord => {
  cookie.set(cookieName, exposureRecord, {
    domain: '.edmunds.com',
    httpOnly: false,
    secure: true,
    maxAge: TIME.MS.TEN_YEARS,
    path: '/',
  });
};

const getExposureRecordCookie = () => cookie.get(cookieName) ?? '';

export {
  setExposureRecordCookie,
  decodeExposureRecord,
  encodeExposureRecord,
  insertExperimentTuple,
  getExposureRecordCookie,
  mergeExposureRecords,
};
